import * as React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './style.scss';
import { AlpacaPosition } from '../../reducers/positionSlice';
import { Link } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend);

interface PortfolioAllocationProps {
    positions: AlpacaPosition[];
}

export const PortfolioAllocationComponent: React.FC<PortfolioAllocationProps> = ({ positions }) => {
    const [hoveredSymbol, setHoveredSymbol] = React.useState<string | null>(null);
    const [selectedSymbol, setSelectedSymbol] = React.useState<string | null>(null);

    // Generate vibrant, distinct colors
    const generateColors = (count: number) => {
        const colors = [
            'rgb(0, 220, 20)',
            'rgb(255, 80, 0)',
            'rgb(100, 150, 255)',
            'rgb(255, 200, 50)',
            'rgb(150, 100, 255)',
            'rgb(0, 200, 150)',
            'rgb(255, 100, 100)',
            'rgb(100, 200, 255)',
            'rgb(220, 20, 60)',
            'rgb(34, 139, 34)',
            'rgb(255, 140, 0)',
            'rgb(30, 144, 255)',
            'rgb(255, 20, 147)',
            'rgb(0, 191, 255)',
        ];
        const result = [];
        for (let i = 0; i < count; i++) {
            result.push(colors[i % colors.length]);
        }
        return result;
    };

    const colors = generateColors(positions.length);
    const labels = positions.map(p => p.symbol);
    const marketValues = positions.map(p => parseFloat(p.market_value));
    const totalValue = marketValues.reduce((sum, val) => sum + val, 0);
    const sortedPositions = [...positions].sort((a, b) => parseFloat(b.market_value) - parseFloat(a.market_value));
    const topHoldings = sortedPositions.slice(0, 5);

    // Calculate concentration metrics
    const top3Value = topHoldings.slice(0, 3).reduce((sum, p) => sum + parseFloat(p.market_value), 0);
    const concentration = ((top3Value / totalValue) * 100).toFixed(1);
    const avgPosition = (totalValue / positions.length).toFixed(2);

    const data = {
        labels,
        datasets: [
            {
                label: 'Portfolio Allocation',
                data: marketValues,
                backgroundColor: colors,
                borderColor: 'var(--bg-primary)',
                borderWidth: 2,
                hoverOffset: 10,
            },
        ],
    };

    const options: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'right' as const,
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    color: 'var(--text-primary)',
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                padding: 14,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 6,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        const value = context.parsed;
                        const percentage = ((value / totalValue) * 100).toFixed(2);
                        return `$${value.toFixed(2)} (${percentage}%)`;
                    },
                    afterLabel: function (context) {
                        const symbol = labels[context.dataIndex];
                        const pos = positions.find(p => p.symbol === symbol);
                        if (pos) {
                            const change = parseFloat(pos.unrealized_plpc);
                            const changeStr = change >= 0 ? `+${(change * 100).toFixed(2)}%` : `${(change * 100).toFixed(2)}%`;
                            return `P&L: ${changeStr}`;
                        }
                        return '';
                    }
                }
            },
        },
    };

    return (
        <div className="portfolio-allocation">
            {/* Summary Metrics */}
            <div className="summary-metrics">
                <div className="metric">
                    <span className="metric-label">Total Value</span>
                    <span className="metric-value">${totalValue.toFixed(2)}</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Holdings</span>
                    <span className="metric-value">{positions.length}</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Top 3 %</span>
                    <span className="metric-value">{concentration}%</span>
                </div>
                <div className="metric">
                    <span className="metric-label">Avg Position</span>
                    <span className="metric-value">${avgPosition}</span>
                </div>
            </div>

            <div className="allocation-chart-container">
                {positions.length > 0 ? (
                    <Doughnut data={data} options={options} />
                ) : (
                    <div className="no-positions">No positions to display</div>
                )}
            </div>

            {positions.length > 0 && (
                <div className="allocation-details">
                    <div className="details-header">
                        <h3>Top Holdings</h3>
                        <span className="count">{topHoldings.length} largest positions</span>
                    </div>
                    <div className="top-holdings-list">
                        {topHoldings.map((pos, idx) => {
                            const percentage = ((parseFloat(pos.market_value) / totalValue) * 100).toFixed(2);
                            const changePercent = parseFloat(pos.unrealized_plpc);
                            const changeValue = parseFloat(pos.unrealized_pl);

                            return (
                                <div 
                                    key={pos.symbol} 
                                    className={`holding-item ${hoveredSymbol === pos.symbol ? 'hovered' : ''} ${selectedSymbol === pos.symbol ? 'selected' : ''}`}
                                    onMouseEnter={() => setHoveredSymbol(pos.symbol)}
                                    onMouseLeave={() => setHoveredSymbol(null)}
                                    onClick={() => setSelectedSymbol(selectedSymbol === pos.symbol ? null : pos.symbol)}
                                >
                                    <div className="holding-header">
                                        <div className="rank-symbol">
                                            <span className="rank">#{idx + 1}</span>
                                            <span 
                                                className="color-indicator" 
                                                style={{ backgroundColor: colors[positions.findIndex(p => p.symbol === pos.symbol)] }}
                                            ></span>
                                            <Link to={`/symbol/${pos.symbol}`} className="symbol-link">
                                                {pos.symbol}
                                            </Link>
                                        </div>
                                        <span className="qty">{parseFloat(pos.qty).toFixed(2)} shares</span>
                                    </div>
                                    <div className="holding-details">
                                        <div className="detail-row">
                                            <span className="label">Value</span>
                                            <span className="value">${parseFloat(pos.market_value).toFixed(2)}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">Allocation</span>
                                            <div className="allocation-bar">
                                                <div
                                                    className="allocation-fill"
                                                    style={{
                                                        width: `${percentage}%`,
                                                        backgroundColor: colors[positions.findIndex(p => p.symbol === pos.symbol)],
                                                    }}
                                                ></div>
                                                <span className="percentage">{percentage}%</span>
                                            </div>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">Price</span>
                                            <span className="value">${pos.current_price}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">P&L</span>
                                            <span className={`value pnl-value ${changeValue >= 0 ? 'positive' : 'negative'}`}>
                                                ${changeValue.toFixed(2)} ({(changePercent * 100).toFixed(2)}%)
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {positions.length > 5 && (
                <div className="other-holdings">
                    <div className="details-header">
                        <h3>Other Holdings</h3>
                        <span className="count">{positions.length - 5} positions</span>
                    </div>
                    <div className="other-holdings-grid">
                        {sortedPositions.slice(5).map((pos) => {
                            const posIndex = positions.findIndex(p => p.symbol === pos.symbol);
                            const percentage = ((parseFloat(pos.market_value) / totalValue) * 100).toFixed(2);

                            return (
                                <div 
                                    key={pos.symbol} 
                                    className="other-holding-card"
                                    onMouseEnter={() => setHoveredSymbol(pos.symbol)}
                                    onMouseLeave={() => setHoveredSymbol(null)}
                                >
                                    <div className="card-header">
                                        <span 
                                            className="color-dot" 
                                            style={{ backgroundColor: colors[posIndex] }}
                                        ></span>
                                        <Link to={`/symbol/${pos.symbol}`} className="symbol-name">
                                            {pos.symbol}
                                        </Link>
                                    </div>
                                    <div className="card-values">
                                        <div className="card-value">
                                            <span className="small-label">Value</span>
                                            <span className="amount">${parseFloat(pos.market_value).toFixed(2)}</span>
                                        </div>
                                        <div className="card-value">
                                            <span className="small-label">%</span>
                                            <span className="amount">{percentage}%</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
